import type { LensCardData } from '~/types/leLens';

import type { Campaign, Sale, Snapshot, ViewRow } from './data';

export const POST_CARDS = ['campaigns', 'sales', 'views', 'item_history', 'deal_products'];
type SourceRow = Record<string, unknown>;
const object = (value: unknown): SourceRow => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid source row.');
  }
  return value as SourceRow;
};
/** Source batch identity, declared full count and each decoded row are validated before use. */
export function postRows(cards: ReadonlyArray<LensCardData>, id: string): Array<SourceRow> {
  const found = cards.filter((c) => c.cardId === id);
  if (found.length !== 1) {
    throw new Error('Missing or duplicate ' + id + ' source card.');
  }
  const card = found[0];
  if (!['ready', 'empty'].includes(card.status) || card.truncated) {
    throw new Error(card.message ?? id + ' source is incomplete or reached its row limit.');
  }
  if (card.status === 'empty') {
    if (card.rows?.length) {
      throw new Error('Contradictory empty ' + id + ' population.');
    }
    return [];
  }
  const batches = card.rows ?? [];
  if (!batches.length) {
    throw new Error('Missing ' + id + ' source batches.');
  }
  const result: Array<SourceRow> = [],
    indexes = new Set<number>(),
    counts = new Set<number>();
  for (const row of batches) {
    if (
      ['batch', 'record_count', 'source_count'].some(
        (key) => !(typeof row[key] === 'number' || (typeof row[key] === 'string' && row[key].trim() !== '')),
      )
    ) {
      throw new Error('Invalid ' + id + ' batch counters.');
    }
    const batch = Number(row.batch),
      count = Number(row.record_count),
      total = Number(row.source_count);
    if (
      !Number.isSafeInteger(batch) ||
      batch < 0 ||
      indexes.has(batch) ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      count > 1000 ||
      !Number.isSafeInteger(total) ||
      total < count ||
      typeof row.payload !== 'string'
    ) {
      throw new Error('Invalid ' + id + ' source batch.');
    }
    const parsed: unknown = JSON.parse(row.payload);
    if (!Array.isArray(parsed) || parsed.length !== count) {
      throw new Error('Invalid ' + id + ' source batch width.');
    }
    indexes.add(batch);
    counts.add(total);
    result.push(...parsed.map(object));
  }
  if (
    counts.size !== 1 ||
    !counts.has(result.length) ||
    indexes.size !== Math.ceil(result.length / 1000) ||
    [...indexes].some((index) => index >= indexes.size)
  ) {
    throw new Error(id + ' source population is incomplete.');
  }
  return result;
}
function field(row: SourceRow, key: string): unknown {
  if (!(key in row)) {
    throw new Error('Missing source field ' + key);
  }
  return row[key];
}
const nullableText = (row: SourceRow, key: string): string | null => {
  const value = field(row, key);
  if (value === null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null && 'value' in value && typeof value.value === 'string') {
    return value.value;
  }
  throw new Error('Invalid source text ' + key);
};
const text = (row: SourceRow, key: string): string => {
  const v = nullableText(row, key);
  if (v === null) {
    throw new Error('Missing required source text ' + key);
  }
  return v;
};
const day = (row: SourceRow, key: string, nullable = false): string | null => {
  const value = nullableText(row, key);
  if (value === null && nullable) {
    return null;
  }
  if (
    value === null ||
    value.slice(0, 4) === '0000' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    !Number.isFinite(Date.parse(value)) ||
    new Date(value).toISOString().slice(0, 10) !== value
  ) {
    throw new Error('Invalid source date ' + key);
  }
  return value;
};
const numeric = (row: SourceRow, key: string): number | null => {
  const value = field(row, key);
  if (value === null) {
    return null;
  }
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  if (!Number.isFinite(n)) {
    throw new Error('Invalid source number ' + key);
  }
  return n;
};
const bool = (row: SourceRow, key: string): boolean | null => {
  const value = field(row, key);
  if (value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  throw new Error('Invalid source flag ' + key);
};

export function decodeSale(row: SourceRow): Sale {
  return {
    offer_bk: text(row, 'offer_bk'),
    offer_opportunity_name: text(row, 'offer_opportunity_name'),
    vendor_name: text(row, 'vendor_name'),
    date: day(row, 'date'),
    transaction_side: text(row, 'transaction_side'),
    transaction_type: text(row, 'transaction_type'),
    orderKey: nullableText(row, 'orderKey'),
    itemKey: nullableText(row, 'itemKey'),
    order_date: day(row, 'order_date', true),
    package_price_rank: numeric(row, 'package_price_rank'),
    vendor_parent_name: nullableText(row, 'vendor_parent_name'),
    offer_owner_name: nullableText(row, 'offer_owner_name'),
    product_name: nullableText(row, 'product_name'),
    offer_country_name: nullableText(row, 'offer_country_name'),
    offer_geo_area_level_1: nullableText(row, 'offer_geo_area_level_1'),
    brand_name: nullableText(row, 'brand_name'),
    room_type_name: nullableText(row, 'room_type_name'),
    package_name: nullableText(row, 'package_name'),
    package_nights: numeric(row, 'package_nights'),
    number_of_nights: numeric(row, 'number_of_nights'),
    number_of_rooms: numeric(row, 'number_of_rooms'),
    number_of_packages: numeric(row, 'number_of_packages'),
    check_in_date: day(row, 'check_in_date', true),
    check_out_date: day(row, 'check_out_date', true),
    cost_currency: nullableText(row, 'cost_currency'),
    cost_sales: numeric(row, 'cost_sales'),
    cost_sales_aud: numeric(row, 'cost_sales_aud'),
    vendor_holdback_amount: numeric(row, 'vendor_holdback_amount'),
    vendor_holdback_amount_aud: numeric(row, 'vendor_holdback_amount_aud'),
    leadroom: bool(row, 'leadroom'),
    reservation_type_name: nullableText(row, 'reservation_type_name'),
    country_name: nullableText(row, 'country_name'),
    customer_geo_state: nullableText(row, 'customer_geo_state'),
    number_of_adults: numeric(row, 'number_of_adults'),
    number_of_children: numeric(row, 'number_of_children'),
    number_of_infants: numeric(row, 'number_of_infants'),
    inclusion_upgrade_flag: bool(row, 'inclusion_upgrade_flag'),
    upgrade_level: nullableText(row, 'upgrade_level'),
    first_sale_flag: bool(row, 'first_sale_flag'),
    luxplus_member_flag: bool(row, 'luxplus_member_flag'),
    luxplus_order_contains_membership_flag: bool(row, 'luxplus_order_contains_membership_flag'),
    luxplus_offer_member_price_flag: bool(row, 'luxplus_offer_member_price_flag'),
    luxplus_non_member_price_aud: numeric(row, 'luxplus_non_member_price_aud'),
    luxplus_member_price_aud: numeric(row, 'luxplus_member_price_aud'),
    margin_aud: numeric(row, 'margin_aud'),
    margin_aud_refund: numeric(row, 'margin_aud_refund'),
  };
}

export function decodeViewRow(row: SourceRow): ViewRow {
  return {
    offer_bk: text(row, 'offer_bk'),
    date: day(row, 'date'),
    traffic_channel: nullableText(row, 'traffic_channel'),
    website_region: nullableText(row, 'website_region'),
    views: numeric(row, 'views'),
    packages: numeric(row, 'packages'),
    margin: numeric(row, 'margin'),
    marginBefore: numeric(row, 'marginBefore'),
  };
}

export function decodeCampaign(row: SourceRow): Campaign {
  return {
    ...('vendor_name' in row ? { vendor_name: nullableText(row, 'vendor_name') } : {}),
    ...('vendor_parent_name' in row ? { vendor_parent_name: nullableText(row, 'vendor_parent_name') } : {}),
    ...('offer_owner_name' in row ? { offer_owner_name: nullableText(row, 'offer_owner_name') } : {}),
    ...('product_name' in row ? { product_name: nullableText(row, 'product_name') } : {}),
    ...('cost_currency' in row ? { cost_currency: nullableText(row, 'cost_currency') } : {}),
    ...('first_transaction_date' in row ? { first_transaction_date: day(row, 'first_transaction_date') } : {}),
    ...('first_sale_date' in row ? { first_sale_date: day(row, 'first_sale_date', true) } : {}),
    ...('last_transaction_date' in row ? { last_transaction_date: day(row, 'last_transaction_date') } : {}),
    offer_bk: text(row, 'offer_bk'),
    offer_opportunity_name: text(row, 'offer_opportunity_name'),
    offer_country_name: nullableText(row, 'offer_country_name'),
    offer_geo_area_level_1: nullableText(row, 'offer_geo_area_level_1'),
    vendor_bk: nullableText(row, 'vendor_bk'),
    // Catalogue metadata can have no scheduled start. Keep that offer visible but
    // unavailable for automatic report selection; transaction dates stay strict.
    start_date: day(row, 'start_date', true) ?? '',
    end_date: day(row, 'end_date', true),
    campaign_type: nullableText(row, 'campaign_type'),
  };
}

export function livePost(cards: ReadonlyArray<LensCardData>, from: string, to: string, bookingAsOf: string): Snapshot {
  const sales = postRows(cards, 'sales').map(decodeSale),
    views = postRows(cards, 'views').map(decodeViewRow),
    campaigns = postRows(cards, 'campaigns').map(decodeCampaign);
  const history = postRows(cards, 'item_history').map((row) => ({
    orderKey: nullableText(row, 'orderKey'),
    itemKey: nullableText(row, 'itemKey'),
    cost_currency: nullableText(row, 'cost_currency'),
    cost_sales: numeric(row, 'cost_sales'),
    cost_sales_aud: numeric(row, 'cost_sales_aud'),
    vendor_holdback_amount: numeric(row, 'vendor_holdback_amount'),
    vendor_holdback_amount_aud: numeric(row, 'vendor_holdback_amount_aud'),
  }));
  const dealProducts = postRows(cards, 'deal_products').map((row) => text(row, 'product_name'));
  return {
    sales,
    views,
    campaigns,
    history,
    dealProducts,
    meta: { asOf: to, bookingAsOf, from, extractedAt: '', sources: {} },
  };
}
